import { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Loader2, Euro, TrendingUp, Fuel, Wrench, Shield, Package } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const BUCKET_META = {
  leasing:     { label: "Leasing / LOA / LLD", icon: Package,       color: "text-blue-600",   bg: "bg-blue-50" },
  assurance:   { label: "Assurance",           icon: Shield,        color: "text-emerald-600", bg: "bg-emerald-50" },
  maintenance: { label: "Maintenance",         icon: Wrench,        color: "text-orange-600", bg: "bg-orange-50" },
  autre:       { label: "Autre",               icon: Fuel,          color: "text-gray-600",   bg: "bg-gray-50" },
};

const fmt = (v) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v || 0);

export default function VehicleTcoCard({ vehicleId, token }) {
  const [tco, setTco] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!vehicleId || !token) return;
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/api/fleet/vehicles/${vehicleId}/tco`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setTco(res.data);
      } catch (e) {
        console.error("TCO fetch error:", e);
      } finally {
        setLoading(false);
      }
    })();
  }, [vehicleId, token]);

  if (loading) {
    return (
      <Card className="bg-white border">
        <CardContent className="p-6 flex items-center justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
        </CardContent>
      </Card>
    );
  }

  if (!tco || tco.contracts_count === 0) {
    return (
      <Card className="bg-white border" data-testid="tco-card-empty">
        <CardContent className="p-6 text-center text-sm text-muted-foreground">
          <Package className="w-8 h-8 mx-auto mb-2 opacity-40" />
          Aucun contrat associé — le TCO sera calculé automatiquement dès qu&apos;un contrat sera lié.
        </CardContent>
      </Card>
    );
  }

  const bucketEntries = Object.entries(tco.breakdown).filter(([, v]) => v > 0);
  const totalMonthlyContracts = bucketEntries.reduce((s, [, v]) => s + v, 0);

  return (
    <Card className="bg-white border" data-testid="tco-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-semibold flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-blue-600" />
          Coût Total de Possession (TCO)
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          Agrège {tco.contracts_count} contrat{tco.contracts_count > 1 ? "s" : ""} actif{tco.contracts_count > 1 ? "s" : ""}
          {tco.other_costs_12m_total > 0 ? " + dépenses ponctuelles 12 mois" : ""}
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* 3 grandes métriques */}
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-blue-50 rounded-lg p-3">
            <p className="text-xs text-blue-900/70 uppercase tracking-wider">Par mois</p>
            <p className="text-xl font-bold text-blue-900" data-testid="tco-monthly">{fmt(tco.total_monthly)}</p>
          </div>
          <div className="bg-indigo-50 rounded-lg p-3">
            <p className="text-xs text-indigo-900/70 uppercase tracking-wider">Par an</p>
            <p className="text-xl font-bold text-indigo-900" data-testid="tco-annual">{fmt(tco.total_annual)}</p>
          </div>
          <div className={`rounded-lg p-3 ${tco.tco_per_km ? "bg-purple-50" : "bg-gray-100"}`}>
            <p className="text-xs uppercase tracking-wider text-muted-foreground">Par km</p>
            <p className="text-xl font-bold text-purple-900" data-testid="tco-per-km">
              {tco.tco_per_km ? `${tco.tco_per_km.toFixed(3)} €` : "—"}
            </p>
            {tco.annual_allowed_mileage && (
              <p className="text-[10px] text-muted-foreground">Base : {tco.annual_allowed_mileage.toLocaleString("fr-FR")} km/an</p>
            )}
          </div>
        </div>

        {/* Breakdown par bucket */}
        <div>
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">Décomposition mensuelle</p>
          <div className="space-y-1.5">
            {bucketEntries.map(([bucket, amount]) => {
              const meta = BUCKET_META[bucket] || BUCKET_META.autre;
              const Icon = meta.icon;
              const pct = totalMonthlyContracts > 0 ? (amount / totalMonthlyContracts * 100) : 0;
              return (
                <div key={bucket} className="flex items-center gap-2 text-sm" data-testid={`tco-bucket-${bucket}`}>
                  <div className={`p-1.5 rounded ${meta.bg}`}>
                    <Icon className={`w-3.5 h-3.5 ${meta.color}`} />
                  </div>
                  <span className="flex-1 text-muted-foreground">{meta.label}</span>
                  <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden max-w-[120px]">
                    <div className={`h-full ${meta.color.replace("text-","bg-")}`} style={{ width: `${pct}%` }} />
                  </div>
                  <span className="font-mono font-semibold text-foreground w-24 text-right">{fmt(amount)}</span>
                </div>
              );
            })}
            {tco.other_costs_monthly_avg > 0 && (
              <div className="flex items-center gap-2 text-sm border-t pt-2 mt-2">
                <div className="p-1.5 rounded bg-yellow-50">
                  <Euro className="w-3.5 h-3.5 text-yellow-600" />
                </div>
                <span className="flex-1 text-muted-foreground">Dépenses ponctuelles (moy./mois sur 12 mois)</span>
                <span className="font-mono font-semibold text-yellow-700 w-24 text-right">{fmt(tco.other_costs_monthly_avg)}</span>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
